import { useState, useEffect, useMemo } from "react";
import styles from "./adm.module.css";

/* ── Back4App 설정 ─────────────────────────────────────────────────────────── */
const PARSE_APP_ID = "iSAfLB3Yu649eDt2gvCbJg9LHU7RbsYhzpdzMLSR";
const PARSE_JS_KEY = "cXuIq8tOiwtopr2fNcEGkpBcnf21kPOLwF5bRqUH";
const PARSE_SERVER = "https://parseapi.back4app.com/";

/* ── 설정값 ────────────────────────────────────────────────────────────────── */
const TIMER_CLASS = "Timer_Setting";
const TIMER_KEY = "setGu";
const TIMER_KEY_VAL = "admin_xo"; // 이 아이디로 로그인하면 전체 메뉴 노출
const TIMER_FIELD = "timeSet";
const RANKING_CLASS = "silence1";
const CODE_CLASS = "silence1_Code";
const CODE_PREFIX = "op"; // 코드 생성 기본 접두어

const TIME_OPTIONS = [
  { label: "20분", value: 1200 },
  { label: "30분", value: 1800 },
  { label: "40분", value: 2400 },
  { label: "60분", value: 3600 },
];

const ROUND_OPTIONS = ["a", "b", "c", "d", "e"];
const ROUND_LABEL = { a: "1", b: "2", c: "3", d: "4", e: "5" };

/* ── 탭 설정 ───────────────────────────────────────────────────────────────── */
const TABS = [
  {
    name: "유저 게임 정보",
    table: RANKING_CLASS,
    cols: ["keyCode", "playerName", "score", "clearTime"],
    labels: {
      keyCode: "사용자 코드",
      playerName: "사용자 닉네임",
      score: "점수",
      clearTime: "클리어시간",
    },
    rankBy: { score: "score", time: "clearTime" },
  },
];

/* ── Parse SDK ─────────────────────────────────────────────────────────────── */
let _parseReady = false;
function loadParse() {
  return new Promise((resolve) => {
    if (window.Parse && _parseReady) return resolve(window.Parse);
    if (window.Parse && !_parseReady) {
      window.Parse.initialize(PARSE_APP_ID, PARSE_JS_KEY);
      window.Parse.serverURL = PARSE_SERVER;
      _parseReady = true;
      return resolve(window.Parse);
    }
    const s = document.createElement("script");
    s.src = "https://npmcdn.com/parse/dist/parse.min.js";
    s.onload = () => {
      window.Parse.initialize(PARSE_APP_ID, PARSE_JS_KEY);
      window.Parse.serverURL = PARSE_SERVER;
      _parseReady = true;
      resolve(window.Parse);
    };
    document.head.appendChild(s);
  });
}

/* xlsx 동적 로드 */
function loadXLSX() {
  return new Promise((resolve) => {
    if (window.XLSX) return resolve(window.XLSX);
    const s = document.createElement("script");
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload = () => resolve(window.XLSX);
    document.head.appendChild(s);
  });
}

/* ── 전체 데이터 fetch ─────────────────────────────────────────────────────── */
async function fetchAll(Parse, tableName) {
  const Class = Parse.Object.extend(tableName);
  const BATCH = 1000;
  let skip = 0,
    all = [],
    hasMore = true;
  while (hasMore) {
    const q = new Parse.Query(Class);
    q.limit(BATCH);
    q.skip(skip);
    q.descending("createdAt");
    const res = await q.find();
    all = all.concat(res);
    hasMore = res.length === BATCH;
    skip += BATCH;
  }
  return all;
}

/* ── 랭킹 계산 ─────────────────────────────────────────────────────────────── */
function calcRanking(rows, rankBy) {
  if (!rankBy) return rows.map((obj, i) => ({ obj, rank: i + 1, no: i + 1 }));
  const { score: sKey, time: tKey } = rankBy;
  const valid = rows.filter((r) => r.get(sKey) != null && r.get(tKey) != null);
  const invalid = rows.filter(
    (r) => r.get(sKey) == null || r.get(tKey) == null,
  );
  valid.sort((a, b) => {
    const sd = b.get(sKey) - a.get(sKey);
    return sd !== 0 ? sd : a.get(tKey) - b.get(tKey);
  });
  let rank = 1;
  const ranked = valid.map((obj, i) => {
    if (i > 0) {
      const prev = valid[i - 1];
      if (
        !(obj.get(sKey) === prev.get(sKey) && obj.get(tKey) === prev.get(tKey))
      )
        rank = i + 1;
    }
    return { obj, rank, no: i + 1 };
  });
  return [
    ...ranked,
    ...invalid.map((obj, i) => ({ obj, rank: "-", no: valid.length + i + 1 })),
  ];
}

/* ── 유틸 ─────────────────────────────────────────────────────────────────── */
function fmt(val) {
  if (val === null || val === undefined)
    return <span style={{ color: "#ccc" }}>-</span>;
  if (typeof val === "boolean") return String(val);
  if (val instanceof Date)
    return val.toISOString().slice(0, 16).replace("T", " ");
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

const formatTime = (sec) => {
  if (sec == null) return "-";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0)
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}분 ${String(s).padStart(2, "0")}초`;
  return `${String(m).padStart(2, "0")}분 ${String(s).padStart(2, "0")}초`;
};

const formatTimeSec = (sec) => {
  if (sec == null) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0
    ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const getRoundFromCode = (code = "") => {
  if (!code) return null;
  const m = code.match(/^[a-z]+([a-e])\d+$/i);
  return m ? m[1].toLowerCase() : null;
};

// introInCode 또는 keyCode에서 회차 추출 시도
const getRoundFromObj = (obj) => {
  const introInCode = obj.get("introInCode") || "";
  const keyCode = obj.get("keyCode") || "";
  return getRoundFromCode(introInCode) || getRoundFromCode(keyCode) || null;
};

/* ── XL 다운로드 ───────────────────────────────────────────────────────────── */
async function downloadRankingXL(rows) {
  const XLSX = await loadXLSX();
  const data = rows.map(({ obj, rank }) => ({
    등수: rank,
    사용자코드: obj.get("keyCode") ?? "",
    닉네임: obj.get("playerName") ?? "",
    점수: obj.get("score") ?? "",
    클리어시간: formatTimeSec(obj.get("clearTime")),
  }));
  const ws = window.XLSX.utils.json_to_sheet(data);
  const wb = window.XLSX.utils.book_new();
  window.XLSX.utils.book_append_sheet(wb, ws, "랭킹");
  window.XLSX.writeFile(wb, `silence1_랭킹_${todayStr()}.xlsx`);
}

async function downloadCodeXL(codeObjs) {
  await loadXLSX();
  const sorted = [...codeObjs].sort((a, b) => {
    if (a.useDate !== b.useDate) return a.useDate.localeCompare(b.useDate);
    if (a.round !== b.round) return a.round.localeCompare(b.round);
    return a.entryCode.localeCompare(b.entryCode);
  });
  const data = sorted.map((o) => ({
    사용일자: o.useDate,
    회차: ROUND_LABEL[o.round] ? `${ROUND_LABEL[o.round]}회차` : o.round,
    접속코드: o.entryCode,
    사용여부: o.usageCount >= 1 ? "사용" : "미사용",
    사용횟수: o.usageCount || 0,
    등록일시: o.createdAt ?? "",
  }));
  const ws = window.XLSX.utils.json_to_sheet(data);
  const wb = window.XLSX.utils.book_new();
  window.XLSX.utils.book_append_sheet(wb, ws, "접속코드");
  window.XLSX.writeFile(wb, `silence1_코드_${todayStr()}.xlsx`);
}

/* ── XL 다운로드 버튼 ──────────────────────────────────────────────────────── */
function XlDownBtn({ onDownload }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await onDownload();
    } catch (e) {
      alert("다운로드 실패: " + e.message);
    }
    setBusy(false);
  };
  return (
    <button
      className={styles.code_f_btn}
      onClick={go}
      disabled={busy}
      style={{
        background: "#1a73e8",
        color: "#fff",
        border: "none",
        fontWeight: 500,
      }}
    >
      {busy ? "생성 중…" : "XL 다운로드"}
    </button>
  );
}

/* ── Login ─────────────────────────────────────────────────────────────────── */
function LoginScreen({ onLogin }) {
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);

  const go = async () => {
    if (!id || !pw) return setErr("아이디와 비밀번호를 입력하세요.");
    setBusy(true);
    setErr("");
    try {
      const Parse = await loadParse();
      const user = await Parse.User.logIn(id, pw);
      onLogin(user);
    } catch (e) {
      setErr(
        e.code === 101
          ? "아이디 또는 비밀번호가 올바르지 않습니다."
          : `오류: ${e.message}`,
      );
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.login_box}>
      <div className={styles.login_bb}>
        <div className={styles.login_b}>
          <div
            className={styles.login_b0}
            style={{ animation: shake ? "shake .4s ease" : "fadeUp .5s ease" }}
          >
            <p className={styles.p_tit01}>관리자 로그인</p>
            <div className={styles.login_row}>
              <label className={styles.login_label}>아이디</label>
              <input
                className={styles.login_input}
                type="text"
                value={id}
                placeholder="아이디를 입력하세요"
                onChange={(e) => {
                  setId(e.target.value);
                  setErr("");
                }}
                onKeyDown={(e) => e.key === "Enter" && go()}
              />
            </div>
            <div className={styles.login_row}>
              <label className={styles.login_label}>비밀번호</label>
              <input
                className={styles.login_input}
                type="password"
                value={pw}
                placeholder="비밀번호를 입력하세요"
                onChange={(e) => {
                  setPw(e.target.value);
                  setErr("");
                }}
                onKeyDown={(e) => e.key === "Enter" && go()}
              />
            </div>
            {err && <p className={styles.login_error}>{err}</p>}
            <input
              className={styles.login_btn01}
              type="button"
              value={busy ? "로그인 중…" : "로그인"}
              onClick={go}
              disabled={busy}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── TimeSetPanel ──────────────────────────────────────────────────────────── */
function TimeSetPanel() {
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const Parse = await loadParse();
      const q = new Parse.Query(TIMER_CLASS);
      q.equalTo(TIMER_KEY, TIMER_KEY_VAL);
      const r = await q.first();
      if (r) setCurrent(r.get(TIMER_FIELD) ?? null);
    } catch (e) {
      console.warn("timeSet 로드 실패:", e.message);
    }
  };

  const save = async (val) => {
    setSaving(true);
    setMsg("");
    try {
      const Parse = await loadParse();
      const q = new Parse.Query(TIMER_CLASS);
      q.equalTo(TIMER_KEY, TIMER_KEY_VAL);
      let r = await q.first();
      if (!r) {
        const Obj = Parse.Object.extend(TIMER_CLASS);
        r = new Obj();
        r.set(TIMER_KEY, TIMER_KEY_VAL);
      }
      r.set(TIMER_FIELD, val);
      await r.save();
      setCurrent(val);
      setMsg("저장되었습니다.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(`오류: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className={styles.code_f}>
        <span className={styles.code_b_tit}>게임 시간 설정</span>
      </div>
      <div style={{ padding: "32px 28px" }}>
        <div
          style={{
            background: "#f5f8ff",
            border: "0.5px solid #d0e0ff",
            borderRadius: 12,
            padding: "24px 28px",
            marginBottom: 20,
            maxWidth: 480,
          }}
        >
          <p
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#888",
              marginBottom: 12,
            }}
          >
            현재 설정
          </p>
          <p
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: "#1a73e8",
              marginBottom: 4,
            }}
          >
            {current != null ? `${current / 60}분` : "미설정"}
          </p>
          <p style={{ fontSize: 13, color: "#aaa" }}>
            {current != null
              ? `${current}초 · 게임 진행 시간`
              : "아래에서 시간을 선택하세요"}
          </p>
        </div>

        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: 12,
            padding: "24px 28px",
            maxWidth: 480,
          }}
        >
          <p
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#888",
              marginBottom: 16,
            }}
          >
            시간 선택
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => save(opt.value)}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: "16px 0",
                  borderRadius: 10,
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  border:
                    current === opt.value
                      ? "2px solid #1a73e8"
                      : "1.5px solid #e0e0e0",
                  background: current === opt.value ? "#e8f0fe" : "#fafafa",
                  color: current === opt.value ? "#1a73e8" : "#555",
                  transition: "all .15s",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {msg && (
            <p style={{ marginTop: 14, fontSize: 13, color: "#2e7d32" }}>
              ✓ {msg}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

/* ── CodeGenPanel ──────────────────────────────────────────────────────────── */
function CodeGenPanel() {
  const [innerTab, setInnerTab] = useState("list");
  const [prefix, setPrefix] = useState(CODE_PREFIX);
  const [useDate, setUseDate] = useState(() => todayStr());
  const [round, setRound] = useState("a");
  const [count, setCount] = useState(10);
  const [preview, setPreview] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });
  const [codeObjs, setCodeObjs] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [openKeys, setOpenKeys] = useState(() => ({
    [`d:${todayStr()}`]: true,
  }));

  const buildCodes = () => {
    const p = (prefix || CODE_PREFIX).toLowerCase().trim();
    const n = Math.min(999, Math.max(1, Number(count) || 1));
    return Array.from(
      { length: n },
      (_, i) => `${p}${round}${String(i + 1).padStart(3, "0")}`,
    );
  };

  const doPreview = () => {
    setPreview(buildCodes());
    setMsg({ text: "", ok: true });
  };

  const doSave = async () => {
    if (!useDate) {
      setMsg({ text: "사용 일자를 선택하세요.", ok: false });
      return;
    }
    setSaving(true);
    setMsg({ text: "", ok: true });
    try {
      const Parse = await loadParse();
      const CodeClass = Parse.Object.extend(CODE_CLASS);
      const codes = buildCodes();
      const existQ = new Parse.Query(CodeClass);
      existQ.equalTo("useDate", useDate);
      existQ.containedIn("entryCode", codes);
      const exists = await existQ.find();
      const existSet = new Set(exists.map((o) => o.get("entryCode")));
      const newCodes = codes.filter((c) => !existSet.has(c));
      if (newCodes.length === 0) {
        setMsg({ text: "해당 날짜에 모든 코드가 이미 존재합니다.", ok: false });
        setSaving(false);
        return;
      }
      const objs = newCodes.map((c) => {
        const o = new CodeClass();
        o.set("entryCode", c);
        o.set("useDate", useDate);
        o.set("round", round);
        o.set("usageCount", 0);
        return o;
      });
      await Parse.Object.saveAll(objs);
      setMsg({
        text: `✓ ${newCodes.length}개 저장 완료 (${useDate} / ${ROUND_LABEL[round]}회차)`,
        ok: true,
      });
      setPreview(buildCodes());
      loadList();
    } catch (e) {
      setMsg({ text: `오류: ${e.message}`, ok: false });
    } finally {
      setSaving(false);
    }
  };

  const loadList = async () => {
    setListLoading(true);
    try {
      const Parse = await loadParse();
      const all = await fetchAll(Parse, CODE_CLASS);
      setCodeObjs(
        all.map((obj) => ({
          entryCode: obj.get("entryCode") || "",
          useDate: obj.get("useDate") || "미분류",
          round: obj.get("round") || "-",
          usageCount: obj.get("usageCount") || 0,
          createdAt:
            obj.createdAt?.toISOString().slice(0, 16).replace("T", " ") ?? "",
        })),
      );
    } catch (e) {
      console.warn("목록 로드 실패:", e.message);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    codeObjs.forEach((o) => {
      const key = `${o.useDate}||${o.round}`;
      if (!map[key])
        map[key] = { useDate: o.useDate, round: o.round, codes: [] };
      map[key].codes.push(o);
    });
    Object.values(map).forEach((g) =>
      g.codes.sort((a, b) => a.entryCode.localeCompare(b.entryCode)),
    );
    return Object.values(map).sort((a, b) => {
      if (a.useDate !== b.useDate) return b.useDate.localeCompare(a.useDate);
      return a.round.localeCompare(b.round);
    });
  }, [codeObjs]);

  const byDate = useMemo(() => {
    const map = {};
    grouped.forEach((g) => {
      if (!map[g.useDate]) map[g.useDate] = [];
      map[g.useDate].push(g);
    });
    return map;
  }, [grouped]);

  const toggleKey = (k) => setOpenKeys((p) => ({ ...p, [k]: !p[k] }));

  const previewN = Math.min(999, Math.max(1, Number(count) || 1));
  const previewP = (prefix || CODE_PREFIX).toLowerCase().trim();

  const S = {
    label: { fontSize: 13, color: "#888", display: "block", marginBottom: 4 },
    input: {
      width: "100%",
      padding: "7px 10px",
      border: "0.5px solid #ddd",
      borderRadius: 6,
      fontSize: 14,
      boxSizing: "border-box",
    },
    chip: {
      display: "inline-block",
      fontSize: 11,
      fontFamily: "monospace",
      background: "#f5f5f5",
      border: "0.5px solid #ddd",
      borderRadius: 4,
      padding: "2px 7px",
    },
    chipUsed: {
      display: "inline-block",
      fontSize: 11,
      fontFamily: "monospace",
      background: "#fdecea",
      border: "0.5px solid #f5c6cb",
      borderRadius: 4,
      padding: "2px 7px",
      color: "#c62828",
    },
    rBadge: {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 10px",
      borderRadius: 20,
      background: "#e8f0fe",
      color: "#1a73e8",
      display: "inline-block",
    },
    iTab: (a) => ({
      padding: "8px 16px",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: a ? 500 : 400,
      color: a ? "#000" : "#888",
      border: "none",
      borderBottom: a ? "2px solid #000" : "2px solid transparent",
      marginBottom: -1,
      background: "none",
    }),
    btnP: {
      padding: "9px 0",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 500,
      background: "#e8f0fe",
      color: "#1a73e8",
      border: "0.5px solid #a8c7f8",
      borderRadius: 6,
    },
    btnS: {
      padding: "9px 0",
      cursor: "pointer",
      fontSize: 13,
      background: "#f5f5f5",
      color: "#333",
      border: "0.5px solid #ddd",
      borderRadius: 6,
    },
  };

  return (
    <>
      <div className={styles.code_f}>
        <span className={styles.code_b_tit}>접속코드 생성</span>
        {innerTab === "list" && (
          <div className={styles.toolbar_right}>
            <XlDownBtn onDownload={() => downloadCodeXL(codeObjs)} />
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          borderBottom: "0.5px solid #eee",
          padding: "0 24px",
        }}
      >
        {[
          ["gen", "코드 생성"],
          ["list", "생성 목록"],
        ].map(([k, l]) => (
          <button
            key={k}
            style={S.iTab(innerTab === k)}
            onClick={() => {
              setInnerTab(k);
              if (k === "list") loadList();
            }}
          >
            {l}
          </button>
        ))}
      </div>

      <div style={{ padding: "24px" }}>
        {/* 코드 생성 탭 */}
        {innerTab === "gen" && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <label style={S.label}>프로젝트 접두어 (예: sp)</label>
                <input
                  type="text"
                  maxLength={4}
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toLowerCase())}
                  style={S.input}
                  placeholder="sp"
                />
              </div>
              <div>
                <label style={S.label}>사용 일자</label>
                <input
                  type="date"
                  value={useDate}
                  onChange={(e) => setUseDate(e.target.value)}
                  style={S.input}
                />
              </div>
              <div>
                <label style={S.label}>회차</label>
                <select
                  value={round}
                  onChange={(e) => setRound(e.target.value)}
                  style={S.input}
                >
                  {ROUND_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {ROUND_LABEL[r]}회차 ({r})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>생성 개수 (1 ~ 999)</label>
              <input
                type="number"
                min={1}
                max={999}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                style={{ ...S.input, width: 130 }}
              />
            </div>

            <div
              style={{
                background: "#f7f7f7",
                borderRadius: 6,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 12,
                color: "#888",
              }}
            >
              생성 규칙:{" "}
              <strong style={{ color: "#000", fontFamily: "monospace" }}>
                {previewP}
                {round}001
              </strong>
              {" ~ "}
              <strong style={{ color: "#000", fontFamily: "monospace" }}>
                {previewP}
                {round}
                {String(previewN).padStart(3, "0")}
              </strong>
              {" · "}총 {previewN}개 · 사용기한{" "}
              <strong style={{ color: "#1a73e8" }}>{useDate || "-"}</strong>
            </div>

            {preview.length > 0 && (
              <div
                style={{
                  background: "#f7f7f7",
                  border: "0.5px solid #ddd",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 16,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 5,
                }}
              >
                {preview.slice(0, 30).map((c) => (
                  <span key={c} style={S.chip}>
                    {c}
                  </span>
                ))}
                {preview.length > 30 && (
                  <span style={{ ...S.chip, color: "#aaa" }}>
                    +{preview.length - 30}개 더
                  </span>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button style={{ ...S.btnS, flex: 1 }} onClick={doPreview}>
                미리보기
              </button>
              <button
                style={{ ...S.btnP, flex: 2 }}
                onClick={doSave}
                disabled={saving}
              >
                {saving ? "저장 중…" : "생성 및 저장"}
              </button>
            </div>
            {msg.text && (
              <p
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  color: msg.ok ? "#2e7d32" : "#c62828",
                }}
              >
                {msg.text}
              </p>
            )}
          </>
        )}

        {/* 생성 목록 탭 */}
        {innerTab === "list" && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, color: "#888" }}>
                총 {codeObjs.length}개
              </span>
              <span style={S.chip}>spa001</span>
              <span style={{ fontSize: 12, color: "#888" }}>미사용</span>
              <span style={S.chipUsed}>spa001 ✓</span>
              <span style={{ fontSize: 12, color: "#c62828" }}>사용됨</span>
              <button
                style={{
                  ...S.btnS,
                  padding: "5px 12px",
                  width: "auto",
                  marginLeft: "auto",
                }}
                onClick={loadList}
                disabled={listLoading}
              >
                {listLoading ? "로딩 중…" : "새로고침"}
              </button>
            </div>

            {listLoading ? (
              <p
                style={{
                  textAlign: "center",
                  color: "#aaa",
                  padding: "2rem 0",
                  fontSize: 13,
                }}
              >
                불러오는 중…
              </p>
            ) : Object.keys(byDate).length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  color: "#aaa",
                  padding: "2rem 0",
                  fontSize: 13,
                }}
              >
                생성된 코드가 없습니다.
              </p>
            ) : (
              Object.keys(byDate)
                .sort()
                .reverse()
                .map((date) => {
                  const groups = byDate[date];
                  const total = groups.reduce((s, g) => s + g.codes.length, 0);
                  const dateOpen = !!openKeys[`d:${date}`];
                  return (
                    <div
                      key={date}
                      style={{
                        border: "0.5px solid #eee",
                        borderRadius: 10,
                        marginBottom: 10,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        onClick={() => toggleKey(`d:${date}`)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 18px",
                          background: "#f9f9f9",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 600 }}>
                          {date}
                        </span>
                        <span style={{ fontSize: 12, color: "#aaa" }}>
                          총 {total}명 · {groups.length}개 회차{" "}
                          {dateOpen ? "▲" : "▼"}
                        </span>
                      </div>

                      {dateOpen && (
                        <div style={{ padding: "10px 18px 14px" }}>
                          {groups.map((g) => {
                            const gKey = `${g.useDate}||${g.round}`;
                            const gOpen = !!openKeys[`g:${gKey}`];
                            const usedCount = g.codes.filter(
                              (o) => (o?.usageCount || 0) >= 1,
                            ).length;
                            return (
                              <div
                                key={gKey}
                                style={{
                                  marginBottom: 8,
                                  border: "0.5px solid #f0f0f0",
                                  borderRadius: 8,
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  onClick={() => toggleKey(`g:${gKey}`)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "9px 14px",
                                    background: "#fafafa",
                                    cursor: "pointer",
                                  }}
                                >
                                  <span style={S.rBadge}>
                                    {ROUND_LABEL[g.round] ?? g.round}회차
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 13,
                                      color: "#333",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {g.codes.length}명
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: usedCount > 0 ? "#c62828" : "#aaa",
                                    }}
                                  >
                                    사용 {usedCount}/{g.codes.length}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: "#aaa",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    {g.codes[0]?.entryCode} ~{" "}
                                    {g.codes[g.codes.length - 1]?.entryCode}{" "}
                                    {gOpen ? "▲" : "▼"}
                                  </span>
                                </div>
                                {gOpen && (
                                  <div
                                    style={{
                                      padding: "8px 14px 12px",
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 4,
                                    }}
                                  >
                                    {g.codes.map((o) => {
                                      const used = (o?.usageCount || 0) >= 1;
                                      return (
                                        <span
                                          key={o.entryCode}
                                          style={used ? S.chipUsed : S.chip}
                                        >
                                          {o.entryCode}
                                          {used && " ✓"}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ── RankingPanel (기존 TableView 대체) ────────────────────────────────────── */
const PER = 10;

function RankingPanel() {
  const [allRanked, setAllRanked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterRound, setFilterRound] = useState("all");
  const [filterDate, setFilterDate] = useState("");

  const tab = TABS[0];

  const loadAll = async () => {
    setLoading(true);
    setErr("");
    try {
      const Parse = await loadParse();
      const all = await fetchAll(Parse, RANKING_CLASS);
      setAllRanked(calcRanking(all, tab.rankBy));
      setPage(1);
    } catch (e) {
      setErr(`데이터를 불러오지 못했습니다: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filtered = useMemo(() => {
    return allRanked.filter(({ obj }) => {
      if (filterRound !== "all") {
        const rnd = getRoundFromObj(obj);
        if (rnd !== filterRound) return false;
      }
      if (filterDate) {
        const d = obj.createdAt?.toISOString().slice(0, 10);
        if (d !== filterDate) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return tab.cols.some((c) => {
          const v = c === "objectId" ? obj.id : obj.get(c);
          return v != null && String(v).toLowerCase().includes(q);
        });
      }
      return true;
    });
  }, [allRanked, filterRound, filterDate, search]);

  /* 필터 후 재랭킹 */
  const reRanked = useMemo(() => {
    const sKey = tab.rankBy.score;
    const tKey = tab.rankBy.time;
    const valids = filtered.filter(
      ({ obj }) => obj.get(sKey) != null && obj.get(tKey) != null,
    );
    const invalids = filtered.filter(
      ({ obj }) => obj.get(sKey) == null || obj.get(tKey) == null,
    );
    let rank = 1;
    return [
      ...valids.map((item, i) => {
        if (i > 0) {
          const prev = valids[i - 1].obj;
          if (
            !(
              item.obj.get(sKey) === prev.get(sKey) &&
              item.obj.get(tKey) === prev.get(tKey)
            )
          )
            rank = i + 1;
        }
        return { ...item, rank, no: i + 1 };
      }),
      ...invalids.map((item, i) => ({
        ...item,
        rank: "-",
        no: valids.length + i + 1,
      })),
    ];
  }, [filtered]);

  const totalPages = Math.ceil(reRanked.length / PER) || 1;
  const pageRows = reRanked.slice((page - 1) * PER, page * PER);

  const pageNums = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (page >= totalPages - 3)
      return [
        1,
        "…",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [1, "…", page - 1, page, page + 1, "…", totalPages];
  };

  const rankColor = (r) =>
    r === 1 ? "#f4b400" : r === 2 ? "#9e9e9e" : r === 3 ? "#cd7f32" : "inherit";

  return (
    <>
      <div className={styles.code_f}>
        <span className={styles.code_b_tit}>
          유저 게임 정보
          <span className={styles.toolbar_count}> {reRanked.length}건</span>
        </span>
        <div className={styles.toolbar_right}>
          <XlDownBtn onDownload={() => downloadRankingXL(reRanked)} />
          <button className={styles.code_f_btn} onClick={loadAll}>
            새로고침
          </button>
          <input
            className={styles.code_f_input}
            placeholder="검색…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {err && <div className={styles.error_banner}>⚠️ {err}</div>}

      {/* 회차 + 날짜 필터 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 24px 0",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 12, color: "#aaa" }}>회차</span>
        {["all", ...ROUND_OPTIONS].map((r) => (
          <button
            key={r}
            onClick={() => {
              setFilterRound(r);
              setPage(1);
            }}
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              cursor: "pointer",
              border: "none",
              background: filterRound === r ? "#1a73e8" : "#f0f0f0",
              color: filterRound === r ? "#fff" : "#555",
              fontWeight: filterRound === r ? 600 : 400,
            }}
          >
            {r === "all" ? "전체" : `${ROUND_LABEL[r]}회차`}
          </button>
        ))}
        <span style={{ fontSize: 12, color: "#aaa", marginLeft: 8 }}>날짜</span>
        <input
          type="date"
          value={filterDate}
          onChange={(e) => {
            setFilterDate(e.target.value);
            setPage(1);
          }}
          style={{
            fontSize: 12,
            padding: "4px 8px",
            border: "0.5px solid #ddd",
            borderRadius: 6,
          }}
        />
        {filterDate && (
          <button
            onClick={() => {
              setFilterDate("");
              setPage(1);
            }}
            style={{
              fontSize: 12,
              padding: "4px 10px",
              borderRadius: 20,
              background: "#f5f5f5",
              border: "none",
              cursor: "pointer",
              color: "#e53935",
            }}
          >
            ✕ 초기화
          </button>
        )}
      </div>

      <div className={styles.code_table}>
        <table className={styles.code_t}>
          <thead>
            <tr>
              <th>No</th>
              {tab.cols.map((c) => (
                <th key={c}>{tab.labels[c] ?? c}</th>
              ))}
              <th>등수</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.td_empty} colSpan={tab.cols.length + 2}>
                  불러오는 중…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td className={styles.td_empty} colSpan={tab.cols.length + 2}>
                  {search ? "검색 결과가 없습니다." : "데이터가 없습니다."}
                </td>
              </tr>
            ) : (
              pageRows.map(({ obj, rank }, idx) => (
                <tr key={obj.id}>
                  <td className={styles.td_num}>
                    {reRanked.length - ((page - 1) * PER + idx)}
                  </td>
                  {tab.cols.map((c) => (
                    <td key={c}>
                      {c === "clearTime"
                        ? formatTime(obj.get(c))
                        : fmt(obj.get(c))}
                    </td>
                  ))}
                  <td
                    className={styles.td_num}
                    style={{
                      fontWeight: rank !== "-" && rank <= 3 ? 700 : 400,
                      color: rankColor(rank),
                    }}
                  >
                    {rank}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.paging_b}>
        <a
          href="#"
          className={styles.block_btn + " " + styles.prev_left}
          onClick={(e) => {
            e.preventDefault();
            if (page > 1) setPage((p) => p - 1);
          }}
        >
          ◀
        </a>
        {pageNums().map((n, idx) =>
          n === "…" ? (
            <span key={`e${idx}`} style={{ margin: "0 5px", color: "#aaa" }}>
              …
            </span>
          ) : (
            <a
              key={n}
              href="#"
              className={`${styles.p_num}${n === page ? " " + styles.sel : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setPage(n);
              }}
            >
              {n}
            </a>
          ),
        )}
        <a
          href="#"
          className={styles.block_btn}
          onClick={(e) => {
            e.preventDefault();
            if (page < totalPages) setPage((p) => p + 1);
          }}
        >
          ▶
        </a>
      </div>
    </>
  );
}

/* ── Dashboard ──────────────────────────────────────────────────────────────── */
function Dashboard({ currentUser, onLogout }) {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    { name: "유저 게임 정보", key: "ranking" },
    { name: "코드 생성", key: "codegen" },
    { name: "시간 설정", key: "timeset" },
  ];

  const renderContent = () => {
    const cur = tabs[activeTab];
    if (!cur) return null;
    if (cur.key === "ranking") return <RankingPanel />;
    if (cur.key === "codegen") return <CodeGenPanel />;
    if (cur.key === "timeset") return <TimeSetPanel />;
    return null;
  };

  return (
    <div className={styles.content_box}>
      <div className={styles.content_top}>
        Admin Console
        <div className={styles.content_top_right}>
          <span>👤 {currentUser.get("username")}</span>
          <button className={styles.logout_btn} onClick={onLogout}>
            로그아웃
          </button>
        </div>
      </div>
      <div className={styles.content_bottom}>
        <div className={styles.content_b_left}>
          <span className={styles.left_b_tit}>MENU</span>
          {tabs.map((tab, i) => (
            <span
              key={tab.key}
              className={`${styles.left_b_tit2}${activeTab === i ? " " + styles.sel : ""}`}
              onClick={() => setActiveTab(i)}
            >
              {tab.name}
            </span>
          ))}
        </div>
        <div className={styles.content_b_right}>{renderContent()}</div>
      </div>
    </div>
  );
}

/* ── App ────────────────────────────────────────────────────────────────────── */
export default function App() {
  const [step, setStep] = useState("init");
  const [currentUser, setCU] = useState(null);

  useEffect(() => {
    loadParse().then((Parse) => {
      const cur = Parse.User.current();
      if (cur) {
        setCU(cur);
        setStep("dashboard");
      } else setStep("login");
    });
  }, []);

  const handleLogin = (user) => {
    setCU(user);
    setStep("dashboard");
  };
  const handleLogout = async () => {
    const Parse = await loadParse();
    await Parse.User.logOut();
    setCU(null);
    setStep("login");
  };

  return (
    <div className={styles.adm}>
      {step === "init" && <div className={styles.init_screen}>초기화 중…</div>}
      {step === "login" && <LoginScreen onLogin={handleLogin} />}
      {step === "dashboard" && (
        <Dashboard currentUser={currentUser} onLogout={handleLogout} />
      )}
    </div>
  );
}
